/**
 * Canonical meeting-time resolver.
 *
 * After backend ships GLE-8044 PR2, ICS-sourced BMCE.start_time becomes the
 * authoritative meeting time, with follow_up_time and meeting_time kept as
 * legacy fallbacks for records that pre-date the migration.
 */

export interface BMCESnapshotLike {
  start_time?: string | null;
  canceled_at?: string | null;
}

export interface WithCanonicalTime {
  canonical_meeting_time?: string | null;
  follow_up_time?: string | null;
  meeting_time?: string | null;
  bmce?: BMCESnapshotLike | null;
}

export interface PartitionUpcomingResultI<T> {
  pinned: T | null;
  others: T[];
  total: number;
}

const firstNonEmpty = (
  ...values: Array<string | null | undefined>
): string | null => {
  for (const value of values) {
    if (value) {
      return value;
    }
  }

  return null;
};

export const getCanonicalMeetingTime = (
  item: WithCanonicalTime
): string | null => {
  return firstNonEmpty(
    item.bmce?.start_time,
    item.canonical_meeting_time,
    item.follow_up_time,
    item.meeting_time
  );
};

export const isMeetingUpcoming = (item: WithCanonicalTime): boolean => {
  const time = getCanonicalMeetingTime(item);

  if (!time) {
    return false;
  }

  return new Date(time).getTime() > Date.now();
};

const defaultIsCanceled = (item: WithCanonicalTime): boolean => {
  return !!item.bmce?.canceled_at;
};

/**
 * Partitions a list of meeting-like items into a pinned soonest upcoming entry
 * and the remaining upcoming entries. Past and canceled items are excluded.
 *
 * Used by the account-details drawer to surface the next meeting at the top of
 * the activity log (GLE-8044 PR5).
 */
export const partitionUpcomingMeetings = <T extends WithCanonicalTime>(
  items: T[],
  options?: { isCanceled?: (item: T) => boolean }
): PartitionUpcomingResultI<T> => {
  const isCanceled = options?.isCanceled ?? defaultIsCanceled;

  const upcoming = items.filter((item) => {
    if (!isMeetingUpcoming(item)) {
      return false;
    }

    if (isCanceled(item)) {
      return false;
    }

    return true;
  });

  const sorted = [...upcoming].sort((a, b) => {
    const timeA = getCanonicalMeetingTime(a);
    const timeB = getCanonicalMeetingTime(b);

    if (!timeA || !timeB) {
      return 0;
    }

    return new Date(timeA).getTime() - new Date(timeB).getTime();
  });

  if (sorted.length === 0) {
    return { pinned: null, others: [], total: 0 };
  }

  const [pinned, ...others] = sorted;

  return { pinned, others, total: sorted.length };
};
